import { useState } from "react";
import type { RootView } from "../shared/productTypes";
import { FinanceConfigProvider } from "../field-service/context/FinanceConfigContext";
import { WOEmailsProvider } from "../field-service/context/WOEmailsContext";
import { CmmsSidebar, CmmsActivePage } from "./CmmsSidebar";
import { AssetsListPage } from "./pages/AssetsListPage";
import { AssetDetailPage } from "./pages/AssetDetailPage";
import { PartsListPage } from "./pages/PartsListPage";
import { WorkOrdersListPage } from "./pages/WorkOrdersListPage";
import { WorkOrderDetailPage } from "./pages/WorkOrderDetailPage";
import { SchedulerPage } from "./pages/SchedulerPage";
import { CompanyDetailPage } from "./pages/CompanyDetailPage";

type CmmsView =
  | { type: "assets-list" }
  | { type: "asset-detail"; assetId: string; assetName: string }
  | { type: "parts-list" }
  | { type: "work-orders-list" }
  | { type: "work-order-detail"; workOrderId: string }
  | { type: "scheduler" }
  | { type: "company-detail"; companyId: string; companyName: string }
  | { type: "placeholder"; page: CmmsActivePage };

function CmmsAppContent({ onNavigateRoot }: { onNavigateRoot: (view: RootView) => void }) {
  const [activePage, setActivePage] = useState<CmmsActivePage>("Assets");
  const [view, setView] = useState<CmmsView>({ type: "assets-list" });

  function handleNavigate(page: CmmsActivePage) {
    setActivePage(page);
    if (page === "Assets") setView({ type: "assets-list" });
    else if (page === "Parts") setView({ type: "parts-list" });
    else if (page === "Work Orders" || page === "My Work Orders") setView({ type: "work-orders-list" });
    else if (page === "Scheduler") setView({ type: "scheduler" });
    else if (page === "Companies") setView({ type: "company-detail", companyId: "acme-international", companyName: "Acme International" });
    else setView({ type: "placeholder", page });
  }

  function renderContent() {
    switch (view.type) {
      case "assets-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <AssetsListPage onOpenAsset={(id, name) => setView({ type: "asset-detail", assetId: id, assetName: name })} />
          </div>
        );
      case "asset-detail":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <AssetDetailPage
              assetId={view.assetId}
              assetName={view.assetName}
              onBack={() => setView({ type: "assets-list" })}
            />
          </div>
        );
      case "parts-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <PartsListPage />
          </div>
        );
      case "work-orders-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <WorkOrdersListPage
              onOpenWorkOrder={(id) => setView({ type: "work-order-detail", workOrderId: id })}
              onCreateNew={() => setView({ type: "work-order-detail", workOrderId: "new" })}
            />
          </div>
        );
      case "work-order-detail":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <WorkOrderDetailPage
              workOrderId={view.workOrderId}
              onBack={() => { setActivePage("Work Orders"); setView({ type: "work-orders-list" }); }}
              onCreateQuote={() => {}}
              onCreateInvoice={() => {}}
            />
          </div>
        );
      case "scheduler":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <SchedulerPage />
          </div>
        );
      case "company-detail":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <CompanyDetailPage
              companyId={view.companyId}
              companyName={view.companyName}
              onBack={() => setView({ type: "placeholder", page: "Companies" })}
            />
          </div>
        );
      case "placeholder":
        return (
          <div style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "var(--spacing-s)",
            background: "var(--color-bg-primary)",
          }}>
            <span style={{ fontSize: 40 }}>🚧</span>
            <p style={{ fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", margin: 0 }}>
              {view.page}
            </p>
            <p style={{ fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", margin: 0 }}>
              This page is coming soon.
            </p>
          </div>
        );
    }
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CmmsSidebar activePage={activePage} onNavigate={handleNavigate} onNavigateRoot={onNavigateRoot} />
      {renderContent()}
    </div>
  );
}

export default function CmmsApp({ onNavigateRoot }: { onNavigateRoot: (view: RootView) => void }) {
  return (
    <FinanceConfigProvider>
      <WOEmailsProvider>
        <CmmsAppContent onNavigateRoot={onNavigateRoot} />
      </WOEmailsProvider>
    </FinanceConfigProvider>
  );
}
